import api, { ApiResponse } from './api';

import {
  ConsumerUnit,
  CreateConsumerUnitProps,
  GetConsumerUnitProps,
  UpdateConsumerUnitProps,
  DeleteConsumerUnitProps,
  GetConsumerUnitInvoiceListProps,
  UploadConsumerUnitImageProps,
} from 'entities/ConsumerUnit';
import { Invoice } from 'entities/Invoice';
import { ConsumptionAnalysis } from 'entities/ConsumptionAnalysis';
import { ConsumptionForecastOverview } from 'entities/ConsumptionForecastOverview';
import {
  ApplianceCatalog,
  ConsumptionSimulationResult,
  SimulationItemInput,
} from 'entities/ConsumptionSimulation';

interface GetConsumptionAnalysisResponse {
  success: boolean;
  data: ConsumptionAnalysis;
}

// Histórico por posto, demanda, tendência e previsão de consumo/gasto da UC
// (ver entities/ConsumptionAnalysis). Estado local nos componentes — sem Redux:
// o R$ previsto muda conforme bandeira/tarifa e não deve ficar cacheado.
// `withForecast: false` (relatório, out/2026): sem o gasto previsto — o
// relatório só usa o histórico por posto e a demanda
export const getConsumptionAnalysisService = (
  consumerUnitId: string,
  opts: { withForecast?: boolean } = {}
): ApiResponse<GetConsumptionAnalysisResponse> =>
  api({
    method: 'get',
    url: `consumer-units/${consumerUnitId}/consumption-analysis${
      opts.withForecast === false ? '?forecast=false' : ''
    }`,
    useToken: true,
  });

interface GetConsumptionForecastOverviewResponse {
  success: boolean;
  data: ConsumptionForecastOverview;
}

// Agregado do dashboard (tendência + previsão de gasto de TODAS as UCs).
// Também sem Redux: o R$ é recalcular a cada chamada (PCAT/bandeira vigentes).
export const getConsumptionForecastOverviewService =
  (): ApiResponse<GetConsumptionForecastOverviewResponse> =>
    api({
      method: 'get',
      url: 'consumer-units/consumption-forecast',
      useToken: true,
    });

// ─── Calculadora de Consumo (catálogo + simulação) ───────────────────────────

interface GetApplianceCatalogResponse {
  success: boolean;
  data: ApplianceCatalog;
}

interface SimulateConsumptionResponse {
  success: boolean;
  data: ConsumptionSimulationResult;
}

// Catálogo CONSTANTE (Procel/PBE/ABVE) + convenções de horário da calculadora.
export const getApplianceCatalogService =
  (): ApiResponse<GetApplianceCatalogResponse> =>
    api({
      method: 'get',
      url: 'consumer-units/appliance-catalog',
      useToken: true,
    });

// Simula o consumo (aparelhos e/ou VE) e devolve consumo + impacto na conta
// (PCAT via motor). Nada é persistido.
export const simulateConsumptionService = (
  consumerUnitId: string,
  data: {
    mode: 'simple' | 'advanced';
    items?: SimulationItemInput[];
    ev?: { categoryId?: string; kmPerMonth?: number };
  }
): ApiResponse<SimulateConsumptionResponse> =>
  api({
    method: 'post',
    url: `consumer-units/${consumerUnitId}/consumption-simulation`,
    data,
    useToken: true,
  });

interface GetConsumerUnitResponse {
  success: boolean;
  data: ConsumerUnit;
}

export const getConsumerUnitService = (
  data: GetConsumerUnitProps
): ApiResponse<GetConsumerUnitResponse> =>
  api({
    method: 'get',
    url: `consumer-units/${data.consumerUnitId}`,
    useToken: true,
  });

interface GetConsumerUnitInvoiceListResponse {
  success: boolean;
  data: Invoice[];
}

export const getConsumerUnitInvoiceListService = ({
  consumerUnitId,
}: GetConsumerUnitInvoiceListProps): ApiResponse<GetConsumerUnitInvoiceListResponse> =>
  api({
    method: 'get',
    url: `consumer-units/${consumerUnitId}/invoices`,
    useToken: true,
  });

interface ListConsumerUnitsResponse {
  success: boolean;
  data: ConsumerUnit[];
}

export const listConsumerUnitService =
  (): ApiResponse<ListConsumerUnitsResponse> =>
    api({
      method: 'get',
      url: 'consumer-units',
      useToken: true,
    });

interface CreateConsumerUnitResponse {
  success: boolean;
  data: ConsumerUnit;
}

export const createConsumerUnitService = ({
  file,
  image,
  ...rest
}: CreateConsumerUnitProps): ApiResponse<CreateConsumerUnitResponse> => {
  const data = new FormData();

  if (file) {
    data.append('image', file);
  }

  data.append('name', rest.name);
  data.append('documentNumber', rest.documentNumber);
  data.append('owner', rest.owner);
  data.append('installation', rest.installation);
  data.append('address', rest.address);
  data.append('zipCode', rest.zipCode);
  data.append('cityId', rest.cityId.toString());
  data.append('stateId', rest.stateId.toString());
  data.append('classificationClassId', rest.classificationClassId);
  data.append('classificationSubgroupId', rest.classificationSubgroupId);
  data.append('voltageId', rest.voltageId);
  data.append('connectionId', rest.connectionId);
  data.append('utilityCompanyId', rest.utilityCompanyId);
  data.append('establishmentId', rest.establishmentId);
  data.append('loadProfileId', rest.loadProfileId);
  data.append('seasonality', rest.seasonality ?? '');
  data.append('hasFv', rest.hasFv ? '1' : '0');
  data.append('read', JSON.stringify(rest.read));
  data.append('refMonth', rest.refMonth);
  data.append('plantList', JSON.stringify(rest.plantList));
  data.append('area', rest.area?.toString() || '');

  return api({
    method: 'post',
    url: 'consumer-units',
    data: data,
    useToken: true,
    hasFile: true,
  });
};

interface UpdateConsumerUnitResponse {
  success: boolean;
  data: ConsumerUnit;
}

export const updateConsumerUnitService = ({
  consumerUnitId,
  ...data
}: UpdateConsumerUnitProps): ApiResponse<UpdateConsumerUnitResponse> =>
  api({
    method: 'patch',
    url: `consumer-units/${consumerUnitId}`,
    data,
    useToken: true,
  });

// Orçamento mensal da UC (R$; null remove) — indicador "orçamento × realizado"
// da tela de Análises (out/2026).
export const updateConsumerUnitBudgetService = (
  consumerUnitId: string,
  monthlyBudget: number | null
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'patch',
    url: `consumer-units/${consumerUnitId}/budget`,
    data: { monthlyBudget },
    useToken: true,
  });

// Favorito do imóvel (estrela no Dashboard e na lista, out/2026)
export const updateConsumerUnitFavouriteService = (
  consumerUnitId: string,
  isFavourite: boolean
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'patch',
    url: `consumer-units/${consumerUnitId}/favourite`,
    data: { isFavourite },
    useToken: true,
  });

// Ordem dos imóveis escolhida pelo cliente (arrastar, out/2026)
export const reorderConsumerUnitsService = (
  consumerUnitIds: string[]
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'patch',
    url: 'consumer-units/order',
    data: { consumerUnitIds },
    useToken: true,
  });

interface UploadConsumerUnitImageResponse {
  success: boolean;
  data: ConsumerUnit;
}

export const uploadConsumerUnitImageService = ({
  consumerUnitId,
  file,
}: UploadConsumerUnitImageProps): ApiResponse<UploadConsumerUnitImageResponse> => {
  const data = new FormData();
  data.append('image', file);
  data.append('consumerUnitId', consumerUnitId);

  return api({
    method: 'patch',
    url: 'consumer-units/image',
    data,
    hasFile: true,
    useToken: true,
  });
};

interface DeleteConsumerUnitResponse {
  success: boolean;
  data: ConsumerUnit;
}

export const deleteConsumerUnitService = ({
  consumerUnitId,
}: DeleteConsumerUnitProps): ApiResponse<DeleteConsumerUnitResponse> =>
  api({
    method: 'delete',
    url: `consumer-units/${consumerUnitId}`,
    useToken: true,
  });
